// FileOperations.js - File loading, saving, and export operations
import { loadItineraryFromFile, formatLanguageCode, normalizeNameForPath } from '../ItineraryCreator/utils/storageUtils';
import { sanitizeFilename, generateSafeGuideFilename } from '../ItineraryCreator/utils/filenameSanitizer';
import { initialPoiState, formatItineraryForSave, generateId } from '../ItineraryCreator/utils/utils';
import { prepareItineraryForPublication } from './automaticGuideResearch';

export class FileOperations {
  constructor(dependencies) {
    this.itineraryState = dependencies.itineraryState;
    this.uiState = dependencies.uiState;
  }

  // Save itinerary to file
  saveItineraryToFile = async (itineraryData) => {
    const { language, currentPoi } = this.itineraryState;

    try {
      // Ensure we include any current POI changes before saving
      const currentItinerary = { ...itineraryData };

      // If there's a current POI being edited with an ID, make sure it's updated in the points array
      if (currentPoi && currentPoi.id) {
        const poiIndex = currentItinerary.points.findIndex(p => p.id === currentPoi.id);
        if (poiIndex !== -1) {
          currentItinerary.points[poiIndex] = { ...currentPoi };
        }
      }

      let filename;

      if (currentItinerary.metadata.filename) {
        // Sanitize existing filename to ensure it's safe
        filename = sanitizeFilename(currentItinerary.metadata.filename, { maxLength: Infinity, preserveHyphens: true });

        if (!filename.toLowerCase().endsWith('.json')) {
          filename += '.json';
        }
      } else {
        // Generate safe filename using the sanitizer
        filename = generateSafeGuideFilename(currentItinerary.metadata);
      }

      const itineraryDataWithTranslationInfo = prepareItineraryForPublication({
        ...currentItinerary,
        metadata: {
          ...currentItinerary.metadata,
          parent_guide_id: currentItinerary.metadata.parent_guide_id,
          translated_itinerary: currentItinerary.metadata.translated_itinerary || "no"
        }
      });
      const fileContent = JSON.stringify(itineraryDataWithTranslationInfo, null, 2);

      if ('showSaveFilePicker' in window) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'JSON File',
              accept: { 'application/json': ['.json'] }
            }]
          });

          const writable = await handle.createWritable();
          await writable.write(fileContent);
          await writable.close();

          console.log('Guide saved successfully via File System Access API');
          return true;
        } catch (err) {
          if (err.name === 'AbortError') {
            console.log('Save cancelled by user');
            return false;
          }
          console.warn('File System Access API failed, falling back to download:', err);
        }
      }

      // Fallback to traditional download
      const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(fileContent);
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', filename);
      linkElement.click();

      console.log('Guide saved successfully via download');
      return true;
    } catch (error) {
      console.error('Error saving guide:', error);
      const { setErrors } = this.itineraryState;
      setErrors(prev => ({ ...prev, save: `Failed to save guide: ${error.message}` }));
      return false;
    }
  };

  // Load guide from file
  handleLoad = async (event) => {
    const { setItinerary, setCurrentPoi, setErrors } = this.itineraryState;
    const { setIsLoading, setLoadProgress, closeAllDialogs, setCurrentStep, setActiveTab } = this.uiState;

    const file = event.target.files[0];
    if (!file) return;

    setIsLoading(true);
    setLoadProgress(0);

    try {
      setLoadProgress(25);
      const itineraryData = await loadItineraryFromFile(file);
      setLoadProgress(50);

      if (itineraryData) {
        setLoadProgress(75);
        setItinerary(itineraryData);
        setCurrentPoi(itineraryData.points?.[0] || initialPoiState);
        setErrors({});
        setLoadProgress(100);

        // Navigate to appropriate step/tab
        setCurrentStep(2);
        setActiveTab('metadata');

        console.log('Guide loaded successfully');
      } else {
        throw new Error('Invalid file format');
      }
    } catch (error) {
      console.error('Error loading guide:', error);
      setErrors({ load: `Failed to load guide: ${error.message}` });
    } finally {
      setIsLoading(false);
      setLoadProgress(0);
      closeAllDialogs();
      // Reset file input
      event.target.value = '';
    }
  };

  // Trigger file input for loading
  handleLoadGuide = () => {
    const fileInput = document.getElementById('file-upload-direct');
    if (fileInput) {
      fileInput.click();
    }
  };

  // Save complete guide (export functionality)
  handleSaveCompleteGuide = async () => {
    const { itinerary, getFieldAccess, currentPoi } = this.itineraryState;

    // Check permissions
    const access = getFieldAccess && getFieldAccess('saveJSON');
    if (access && access !== 'edit' && access !== 'full') {
      return;
    }

    try {
      // Ensure we include any current POI changes before saving
      const currentItinerary = { ...itinerary };

      // If there's a current POI being edited with an ID, make sure it's updated in the points array
      if (currentPoi && currentPoi.id) {
        const poiIndex = currentItinerary.points.findIndex(p => p.id === currentPoi.id);
        if (poiIndex !== -1) {
          currentItinerary.points[poiIndex] = { ...currentPoi };
        }
      }

      // Use the proper formatting function to ensure all fields are included
      const formattedItinerary = prepareItineraryForPublication(
        formatItineraryForSave(currentItinerary)
      );

      // Create complete guide data with proper formatting
      const completeGuideData = {
        ...formattedItinerary,
        exportedAt: new Date().toISOString(),
        version: "1.0"
      };

      const dataStr = JSON.stringify(completeGuideData, null, 2);

      // Use existing filename if available, otherwise generate safe filename
      console.log('🔍 Save Complete Guide - metadata:', itinerary.metadata);
      console.log('🔍 Save Complete Guide - existing filename:', itinerary.metadata?.filename);

      const suggestedName = itinerary.metadata?.filename
        ? sanitizeFilename(itinerary.metadata.filename, { maxLength: Infinity, preserveHyphens: true })
        : generateSafeGuideFilename(itinerary.metadata || {});

      console.log('🔍 Save Complete Guide - suggested filename:', suggestedName);

      // Try to use the modern File System Access API
      if ('showSaveFilePicker' in window) {
        try {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: suggestedName,
            types: [{
              description: 'JSON files',
              accept: { 'application/json': ['.json'] }
            }]
          });

          const writable = await fileHandle.createWritable();
          await writable.write(dataStr);
          await writable.close();

          console.log('Guide saved successfully');
          return;
        } catch (err) {
          if (err.name === 'AbortError') {
            console.log('Save cancelled by user');
            return;
          }
          console.warn('File System Access API failed, falling back to download:', err);
        }
      }

      // Fallback to traditional download
      const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', suggestedName);
      linkElement.click();

    } catch (error) {
      console.error('Error saving guide:', error);
      alert('Failed to save guide. Please try again.');
    }
  };

  // Import guide from multiple .txt files (one per stop)
  // Naming convention: 01_Stop_Name.txt → stop name "Stop Name"
  handleImportFromTxtFiles = async (event) => {
    const { setItinerary, setCurrentPoi, setErrors } = this.itineraryState;
    const { setIsLoading, setLoadProgress, closeAllDialogs, setCurrentStep, setActiveTab } = this.uiState;

    const files = Array.from(event.target.files);
    if (!files.length) return;

    setIsLoading(true);
    setLoadProgress(10);

    try {
      // Sort files by filename so numbering (01_, 02_, ...) is respected
      files.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

      const readFile = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
          reader.readAsText(file, 'UTF-8');
        });

      const guideId = generateId();
      const points = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const content = await readFile(file);

        // Derive stop name: strip leading digits+underscore prefix, replace underscores, strip extension
        const baseName = file.name.replace(/\.txt$/i, '');
        const stopName = baseName
          .replace(/^\d+[_\-\s]+/, '')   // remove leading "01_" or "01-" or "1 "
          .replace(/_/g, ' ')
          .trim();

        const stopIndex = i + 1;
        const poiId = `${guideId}_${stopIndex}`;

        points.push({
          ...initialPoiState,
          id: poiId,
          name: stopName,
          inDepthDescription: content.trim(),
        });

        setLoadProgress(10 + Math.round((i + 1) / files.length * 80));
      }

      const newGuide = {
        metadata: {
          id: guideId,
          name: '',
          description: '',
          country: '',
          city: '',
          coordinates: { latitude: '', longitude: '' },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          language: 'en',
          tier: 'free',
          duration: '',
          difficulty: 'moderate',
          categories: [],
          tags: [],
          thumbnailUrl: '',
          authorId: '',
          authorName: '',
          rating: 0,
          reviewCount: 0,
          theme: '',
          isIntroductoryStop: true,
          ai_generated_personalization: false,
          feedbackCollect: false,
        },
        points,
      };

      setLoadProgress(100);
      setItinerary(newGuide);
      setCurrentPoi(points[0] || initialPoiState);
      setErrors({});

      setCurrentStep(2);
      setActiveTab('metadata');
      closeAllDialogs();

      console.log(`Imported ${points.length} stops from TXT files`);
    } catch (error) {
      console.error('Error importing TXT files:', error);
      setErrors({ load: `Failed to import files: ${error.message}` });
    } finally {
      setIsLoading(false);
      setLoadProgress(0);
      event.target.value = '';
    }
  };

  // Handle image upload
  handleImageUpload = async (event) => {
    const { setCurrentPoi, setErrors } = this.itineraryState;

    const file = event.target.files[0];
    if (!file) return;

    try {
      // Create a data URL for the image
      const reader = new FileReader();
      reader.onload = (e) => {
        setCurrentPoi(prev => ({
          ...prev,
          imageUrl: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      setErrors(prev => ({ ...prev, image: 'Failed to upload image' }));
    }
  };
}
